import apiClient from "../api-client.js";

/**
 * SE Ranking endpoints.
 *
 * Two levels, the same shape as the Google integration and mounted next to it
 * under `/integrations/`:
 *   - a user (`agency_admin` / `consultant`) is linked once to a SE Ranking
 *     sub-account (superadmin only, from the settings page). The link is
 *     personal, not shared by the agency: two consultants of the same agency
 *     have two different sub-accounts, or none at all.
 *   - each client points at one site (project) of the sub-account of whoever
 *     linked it. That link has its own id, and every metric endpoint is a
 *     detail action on it — never on the client id.
 *
 * Only those links live in the database. Every metric below is queried live
 * from SE Ranking on each call — there is no snapshot nor cache in v1, so
 * these calls can be noticeably slower than a plain list endpoint and every
 * caller should show a loading state.
 */

const ACCOUNTS = "/integrations/seranking/accounts";
const SITES = "/integrations/seranking/sites";

/* ------------------------------------------------------------------ */
/* Account level (superadmin)                                          */
/* ------------------------------------------------------------------ */

/**
 * Sub-accounts of the SE Ranking master account.
 *
 * Each one carries `connected` / `connected_user_email`, so a sub-account that
 * already belongs to someone can be greyed out instead of silently failing on
 * save.
 */
export const listSeRankingSubaccounts = async () => {
  const response = await apiClient.get(`${ACCOUNTS}/subaccounts/`);
  return response.data?.sub_accounts || [];
};

/** `agency_admin`/`consultant` users not connected to a sub-account yet. */
export const listConnectableUsers = async () => {
  const response = await apiClient.get(`${ACCOUNTS}/connectable-users/`);
  return response.data?.users || [];
};

/** The existing user↔sub-account connections, flat rows with their own id. */
export const listSeRankingAccounts = async () => {
  const response = await apiClient.get(`${ACCOUNTS}/`);
  const data = response.data;
  return Array.isArray(data) ? data : data?.results || [];
};

/** Connect a user to a sub-account: `{ user, seranking_user_id, seranking_email }`. */
export const connectSeRankingAccount = async (payload) => {
  const response = await apiClient.post(`${ACCOUNTS}/`, payload);
  return response.data;
};

/**
 * Remove a connection, addressed by the connection's own id.
 *
 * Answers 409 with `{ detail, clients }` while clients still hang off the
 * sub-account: the caller shows that list instead of a generic error.
 */
export const disconnectSeRankingAccount = async (connectionId) => {
  await apiClient.delete(`${ACCOUNTS}/${connectionId}/`);
};

/* ------------------------------------------------------------------ */
/* Client ↔ site link                                                  */
/* ------------------------------------------------------------------ */

/**
 * The site linked to a client, or null when it has never been connected.
 *
 * The relation is 1:1, so the list endpoint filtered by client answers with at
 * most one row. Its `id` — not the client id — is what every metric endpoint
 * below is addressed with.
 */
export const getClientSeRankingLink = async (clientId) => {
  const response = await apiClient.get(`${SITES}/`, {
    params: { client: clientId },
  });
  const data = response.data;
  const rows = Array.isArray(data) ? data : data?.results || [];
  return rows[0] || null;
};

/**
 * Projects of the current user's own sub-account that this client can be
 * linked to, as `{ connected, seranking_email, sites }`.
 *
 * `connected: false` means the logged-in user has no SE Ranking sub-account —
 * not that the client has no site — and only a superadmin can fix that, so the
 * caller shows a different message for each case.
 */
export const getSeRankingAvailableSites = async (clientId) => {
  const response = await apiClient.get(`${SITES}/available-sites/`, {
    params: { client: clientId },
  });
  const data = response.data || {};
  return {
    connected: Boolean(data.connected),
    serankingEmail: data.seranking_email || "",
    sites: data.sites || [],
  };
};

/** Link a site to a client, replacing whatever it had. */
export const linkSeRankingSite = async (payload) => {
  const response = await apiClient.post(`${SITES}/`, payload);
  return response.data;
};

/** Unlink, addressed by the link's id (not the client's). */
export const unlinkSeRankingSite = async (linkId) => {
  await apiClient.delete(`${SITES}/${linkId}/`);
};

/* ------------------------------------------------------------------ */
/* Client metrics, all detail actions on the link                      */
/* ------------------------------------------------------------------ */

/**
 * Visibility snapshot plus the daily series behind the chart.
 *
 * Flattened into one object so the panel does not have to know that the
 * backend nests the snapshot under `summary` and the series under
 * `visibility_history`.
 */
export const getSeRankingVisibilitySummary = async (
  linkId,
  { startDate, endDate } = {}
) => {
  const response = await apiClient.get(`${SITES}/${linkId}/visibility-summary/`, {
    params: { start_date: startDate, end_date: endDate },
  });
  const data = response.data || {};
  return {
    siteTitle: data.site_title || "",
    siteDomain: data.site_domain || "",
    ...(data.summary || {}),
    history: data.visibility_history || [],
  };
};

/**
 * The "Visibilidad orgánica" card for one engine and date range.
 *
 * Unlike the visibility summary, every figure honours the range and the
 * engine: visibility and average position series, keywords that improved or
 * declined between both ends of the range, and the cumulative top 3/10/20/100
 * distribution. `engineId` is a site engine id taken from `engines`, or "all"
 * for every engine at once, as SE Ranking's own "all engines" view; omitted,
 * the backend picks Google.
 */
export const getSeRankingOrganicOverview = async (
  linkId,
  { startDate, endDate, engineId } = {}
) => {
  const response = await apiClient.get(`${SITES}/${linkId}/organic-overview/`, {
    params: { start_date: startDate, end_date: endDate, engine: engineId || undefined },
  });
  return response.data;
};

/**
 * Keyword ranking movements, paginated.
 *
 * `change` is the movement between the start and the end of the range, on
 * `engineId` (Google when omitted; "all" gives one row per keyword and engine,
 * each carrying its `engine_id`). `ordering` is a field name, prefixed with
 * "-" for descending; the backend defaults to the biggest movements first.
 * `detail` comes back with an empty `results` when the project tracks engines
 * but not Google, which is a message to show rather than an error.
 */
export const getSeRankingRankings = async (
  linkId,
  { page = 1, pageSize = 10, startDate, endDate, engineId, ordering } = {}
) => {
  const response = await apiClient.get(`${SITES}/${linkId}/rankings/`, {
    params: {
      page,
      page_size: pageSize,
      start_date: startDate,
      end_date: endDate,
      engine: engineId || undefined,
      ordering: ordering || undefined,
    },
  });
  const data = response.data || {};
  return {
    results: data.results || [],
    count: data.count ?? 0,
    detail: data.detail || "",
  };
};

/**
 * One keyword's daily positions, for the chart that opens under its row.
 *
 * `keywordId` is the `keyword_id` of a ranking row. A point with a null
 * `value` means the keyword ranked nowhere that day — a gap in the chart, not
 * a zero. `engineId` is the row's `engine_id`. The backend answers 404 when
 * the keyword is not tracked on that engine, which the caller shows as a
 * message.
 */
export const getSeRankingKeywordHistory = async (
  linkId,
  { keywordId, engineId, startDate, endDate } = {}
) => {
  const response = await apiClient.get(`${SITES}/${linkId}/keyword-history/`, {
    params: {
      keyword_id: keywordId,
      engine: engineId || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    },
  });
  return response.data;
};

/**
 * Backlink totals: `domain_trust` plus `total`, `domains`, `dofollow`,
 * `nofollow`, `anchors`, `ips` and `lost` — `lost` is always null in v1,
 * because no historical total is kept to compare against.
 */
export const getSeRankingBacklinks = async (linkId) => {
  const response = await apiClient.get(`${SITES}/${linkId}/backlinks/`);
  return response.data;
};

/**
 * AI Results Tracker: presence across AI answer engines (Google AI Overview,
 * Google AI Mode, ChatGPT...). `tracked: false` means AIRT has not been set up
 * for this project in SE Ranking yet, not that the request failed.
 */
export const getSeRankingAiVisibility = async (linkId) => {
  const response = await apiClient.get(`${SITES}/${linkId}/ai-visibility/`);
  return response.data;
};

